// Client-facing promotion wording, shared by the Promotions page and the home-screen banner so a
// promotion is described the same way everywhere.

/** Whole-unit money for thresholds ("$500", "£373") — cents on a spend threshold are just noise. */
export function fmtWhole(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${Math.round(Number(amount) || 0)} ${currency || ''}`.trim();
  }
}

/** Promotion dates are whole days set in UTC (00:00 → 23:59:59), so render them in UTC too. */
export const fmtDay = (iso) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', timeZone: 'UTC' });

export const maxPercent = (p) => (p.type === 'TIERED' ? Math.max(0, ...p.tiers.map((t) => t.percent)) : p.percent || 0);

/** One-line offer: "10% off every order" / "Save up to 20% on orders from $500". */
export function offerLine(p, currency) {
  if (p.type !== 'TIERED') return `${p.percent}% off every order`;
  const first = p.tiers[0];
  if (p.tiers.length === 1) {
    return first.minSubtotal > 0 ? `${first.percent}% off orders of ${fmtWhole(first.minSubtotal, currency)} or more` : `${first.percent}% off every order`;
  }
  return `Save up to ${maxPercent(p)}% — from ${first.percent}% off orders of ${fmtWhole(first.minSubtotal, currency)} or more`;
}

/** "Ends 20 November · 18 days left" / "Starts 30 October" / "No end date". */
export function timingLine(p, now = new Date()) {
  if (p.status === 'UPCOMING') return `Starts ${fmtDay(p.startsAt)}${p.endsAt ? `, runs until ${fmtDay(p.endsAt)}` : ''}`;
  if (!p.endsAt) return 'Ongoing — no end date';
  const days = Math.ceil((new Date(p.endsAt) - now) / 864e5);
  const left = days <= 1 ? 'last day' : `${days} days left`;
  return `Ends ${fmtDay(p.endsAt)} · ${left}`;
}

/**
 * Where a cart stands against the LIVE promotions, mirroring the server's resolution (best % wins).
 * Returns { current: { name, percent, saving } | null, next: { name, percent, min, gap, from } | null }
 * where `next` is the nearest threshold that would beat the current rate, and `from` is the
 * threshold the progress bar starts at.
 */
export function promoProgress(promotions, subtotal) {
  const live = (promotions || []).filter((p) => p.status === 'LIVE');
  let current = null;
  for (const p of live) {
    let pct = 0;
    if (p.type === 'TIERED') {
      for (const t of p.tiers) if (subtotal >= t.minSubtotal && t.percent > pct) pct = t.percent;
    } else pct = p.percent || 0;
    if (pct > 0 && (!current || pct > current.percent)) current = { name: p.name, percent: pct, tiered: p.type === 'TIERED' };
  }
  if (current) current.saving = Math.round(subtotal * current.percent) / 100;

  let next = null;
  for (const p of live) {
    if (p.type !== 'TIERED') continue;
    for (const t of p.tiers) {
      if (t.minSubtotal <= subtotal || t.percent <= (current?.percent || 0)) continue;
      if (!next || t.minSubtotal < next.min || (t.minSubtotal === next.min && t.percent > next.percent)) {
        next = { name: p.name, percent: t.percent, min: t.minSubtotal };
      }
    }
  }
  if (next) {
    next.gap = Math.round((next.min - subtotal) * 100) / 100;
    // Bar runs from the tier already reached (or 0) to the next one.
    const reached = live.flatMap((p) => (p.type === 'TIERED' ? p.tiers : [])).filter((t) => t.minSubtotal <= subtotal);
    next.from = reached.length ? Math.max(...reached.map((t) => t.minSubtotal)) : 0;
  }
  return { current, next };
}
